import { lotFromFirestore } from '@parallel-fluidics/shared/models/lots';
import { useQueryClient } from '@tanstack/react-query';
import {
  collection,
  getFirestore,
  orderBy,
  where,
} from 'firebase/firestore';
import useCustomInfiniteQuery from 'hooks/use-customInfiniteQuery';
import { useCallback, useMemo } from 'react';

/**
 * Invalidate every page of the lots list — call after any lot mutation
 * so callers never hand-write the query key.
 */
export function useInvalidateLotsQuery() {
  const queryClient = useQueryClient();
  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: ['getLotsData'] }),
    [queryClient],
  );
}

/**
 * Paginated lots list, newest first. Text filtering is a prefix match on
 * the display code (codes are uppercase `L-YYYYMMDD-###`); the item filter
 * (projectDocId, optionally narrowed by pPartDocId) is server-side equality.
 * Filter combinations are served by Firestore index merging: one
 * (field, orderBy-field) composite per filter field in
 * firestore.indexes.json — no per-combination indexes. Mutations must
 * invalidate the 'getLotsData' key so the list reflects the change.
 */
export default function useLotsQuery({
  filterText = '',
  kind = 'all',
  status = 'all',
  projectDocId = null,
  pPartDocId = null,
  pageSize = 25,
}) {
  const queryParams = useMemo(() => {
    const queryParts = [collection(getFirestore(), 'lots')];
    const normalizedFilter = filterText.trim().toUpperCase();

    if (projectDocId) {
      queryParts.push(where('projectDocId', '==', projectDocId));
      // pPartDocId only narrows a project — an orphan value (hand-edited
      // URL, stale hot-reload state) is ignored rather than issuing a
      // query no composite index serves.
      if (pPartDocId) {
        queryParts.push(where('pPartDocId', '==', pPartDocId));
      }
    }
    if (kind !== 'all') {
      queryParts.push(where('kind', '==', kind));
    }
    if (status !== 'all') {
      queryParts.push(where('status', '==', status));
    }

    if (normalizedFilter) {
      queryParts.push(orderBy('code', 'desc'));
      queryParts.push(where('code', '>=', normalizedFilter));
      queryParts.push(where('code', '<=', `${normalizedFilter}\uf8ff`));
      return queryParts;
    }

    queryParts.push(orderBy('_createdBy.timestamp', 'desc'));
    return queryParts;
  }, [filterText, kind, status, projectDocId, pPartDocId]);

  return useCustomInfiniteQuery({
    queryKey: 'getLotsData',
    queryParams,
    pageSize,
    transformData: lotFromFirestore,
  });
}
